import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

function StarRow({ label, value, set, testid }) {
  return (
    <div className="border border-cream/10 p-4 flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.2em] text-cream/70">{label}</span>
      <div className="flex gap-1" data-testid={testid}>
        {[1,2,3,4,5].map((n) => (
          <button
            key={n}
            data-testid={`${testid}-${n}`}
            onClick={() => set(n)}
            className={`p-1 rounded-full ${value >= n ? "text-gold" : "text-cream/30"}`}
          >
            <Star size={22} weight={value >= n ? "fill" : "regular"} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewForm() {
  const { bid } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(5);
  const [experience, setExperience] = useState(5);
  const [studio, setStudio] = useState(5);
  const [comment, setComment] = useState("");
  const [photoConsent, setPhotoConsent] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bid}`).then((r) => setBooking(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Booking not found"));
  }, [bid]);

  if (err) return <div className="max-w-md mx-auto px-6 py-16 text-cream/70" data-testid="review-error">{String(err)}</div>;
  if (!booking) return <div className="max-w-md mx-auto px-6 py-16 text-cream/50">Loading…</div>;

  if (booking.status !== "completed") {
    return (
      <div className="max-w-lg mx-auto px-6 py-16" data-testid="review-locked">
        <p className="overline mb-3">Reviews are earned</p>
        <h1 className="font-display text-3xl">Only completed verified bookings can leave a review.</h1>
        <p className="text-cream/70 mt-4">Once your appointment is checked out, you'll be able to review Result · Experience · Studio.</p>
        <button onClick={() => nav(-1)} className="btn-ghost mt-8 px-5 py-3 rounded-full text-xs uppercase tracking-[0.2em]">Back</button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="review-submitted">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold text-ink mb-5"><CheckCircle size={30} weight="fill" /></div>
        <h1 className="font-display text-4xl">Thanks — review posted.</h1>
        <p className="text-cream/70 mt-2">Your feedback stays with the verified booking.</p>
        <button onClick={() => nav("/upcoming")} className="btn-gold mt-8 px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Back to bookings</button>
      </div>
    );
  }

  const submit = async () => {
    try {
      await api.post("/reviews", {
        booking_id: bid, result, experience, studio_rating: studio,
        comment, photo_consent: photoConsent, result_photo: photo,
      });
      setSubmitted(true);
      toast.success("Review posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not post review");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-24" data-testid="review-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-cream/70 hover:text-gold mb-6">
        <ArrowLeft size={14} /><span className="text-xs uppercase tracking-[0.2em]">Back</span>
      </button>
      <p className="overline mb-2">Verified review</p>
      <h1 className="font-display text-4xl tracking-tighter">How was {booking.service_name} at {booking.studio_name}?</h1>

      <div className="mt-8 space-y-3">
        <StarRow label="Result" value={result} set={setResult} testid="rate-result" />
        <StarRow label="Experience" value={experience} set={setExperience} testid="rate-experience" />
        <StarRow label="Studio" value={studio} set={setStudio} testid="rate-studio" />
      </div>

      <div className="mt-6">
        <p className="overline mb-2">Written feedback (optional)</p>
        <textarea data-testid="review-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="What worked, what would you say to another Member?"
          className="w-full bg-transparent border border-cream/15 p-3 text-cream placeholder:text-cream/40 focus:border-gold outline-none" />
      </div>

      <div className="mt-6 border border-cream/10 p-4">
        <p className="overline mb-3">Result photo (optional)</p>
        <input type="file" accept="image/*" onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setPhoto(URL.createObjectURL(f));
        }} data-testid="review-photo" className="text-xs text-cream/60" />
        {photo && <img src={photo} alt="result" className="mt-3 w-24 h-24 object-cover rounded-sm border border-cream/15" />}
        <label className="mt-4 flex items-center gap-3 cursor-pointer" data-testid="photo-consent-label">
          <input type="checkbox" data-testid="photo-consent" checked={photoConsent} onChange={(e) => setPhotoConsent(e.target.checked)} className="accent-[#C5A059] w-5 h-5" />
          <span className="text-xs text-cream/80">I consent to making my result photo public on this Studio's profile.</span>
        </label>
      </div>

      <button data-testid="review-submit" onClick={submit} className="btn-gold w-full mt-8 px-6 py-4 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">
        Post verified review
      </button>
    </div>
  );
}
